import { __getTokenStats, SelfCheckOptionsSchema, validateInput, DefineAccentScaleInputSchema, warn, AccentVarsInputSchema, ApplyAccentInputSchema, AssertAccentScaleGradeInputSchema } from './chunk-CDSUSZHW.js';
export { BrandingConfigSchema, __getActivePalette, __getTokenStats, __resetTokenStats, assertBrandingConfig, brandingConfigSchema, defineThemeReader, fromDtf, getCssVar, getCssVars, getTheme, initThemer, isDarkMode, osPrefersDark, resetCssVarWarnings, resolvePreference, setTheme, subscribeTheme, toDtf, validateBrandingConfig } from './chunk-CDSUSZHW.js';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { contrastRatio, hexToOklch, wcagGrade, hexToHsl, hslToHex, apcaContrast } from '@norkron/web-color';
export { contrastRatio as canonicalVariantContrastRatio } from '@norkron/web-color';

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-themer";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selfCheck(root, opts) {
  const start = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const stats = __getTokenStats();
    let totalRequested = 0;
    let totalSatisfied = 0;
    let totalFallbackUsed = 0;
    const uniqueRequested = stats.size;
    for (const [varName, s] of stats) {
      totalRequested += s.requested;
      if (s.satisfied) totalSatisfied += 1;
      if (s.fallbackUsed > 0) totalFallbackUsed += 1;
      const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
      if (now - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "TH_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now - start }
        });
        break;
      }
      if (!s.satisfied) {
        findings.push({
          libId: LIB_ID,
          code: "TH_VAR_MISSING",
          category: "theme",
          severity: "major",
          message: `CSS variable ${varName} requested ${s.requested} time${s.requested === 1 ? "" : "s"} but never satisfied.`,
          suggestedFix: `Define ${varName} on :root (or a [data-theme] selector) in your theme CSS, or pass an explicit fallback to getCssVar().`,
          extras: { variable: varName, requestCount: s.requested, fallbackUsed: s.fallbackUsed }
        });
        continue;
      }
      if (s.fallbackUsed > 0) {
        findings.push({
          libId: LIB_ID,
          code: "TH_VAR_FALLBACK_USED",
          category: "theme",
          severity: "minor",
          message: `CSS variable ${varName} resolved via getCssVar() fallback ${s.fallbackUsed} time${s.fallbackUsed === 1 ? "" : "s"} in this session.`,
          suggestedFix: `Define ${varName} explicitly in your theme CSS to avoid relying on the caller-supplied fallback.`,
          extras: { variable: varName, fallbackUsedCount: s.fallbackUsed, requestCount: s.requested }
        });
      }
    }
    findings.push({
      libId: LIB_ID,
      code: "TH_COVERAGE_REPORT",
      category: "theme",
      severity: "info",
      message: `Token coverage: ${totalSatisfied}/${uniqueRequested} unique vars satisfied, ${totalFallbackUsed} fell back, ${totalRequested} total getCssVar() calls.`,
      extras: {
        uniqueRequested,
        uniqueSatisfied: totalSatisfied,
        uniqueFallbackUsed: totalFallbackUsed,
        totalCalls: totalRequested
      }
    });
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "TH_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/version.ts
var LIB_VERSION = "0.18.1";

// src/bootstrap.ts
function getThemeBootstrapScript(opts = {}) {
  const storageKey = opts.storageKey ?? "norkron-theme";
  const attribute = opts.attribute ?? "data-theme";
  const defaultPref = opts.defaultPreference ?? "auto";
  const themes = opts.themes ? JSON.stringify(opts.themes) : "null";
  const sk = JSON.stringify(storageKey);
  const at = JSON.stringify(attribute);
  const dp = JSON.stringify(defaultPref);
  return `(function(){try{var sk=${sk},at=${at},dp=${dp},th=${themes};var raw=null;try{raw=window.localStorage.getItem(sk);}catch(e){}var pref=raw||dp;if(th&&pref!=="auto"&&th.indexOf(pref)<0){pref=dp;}var resolved=pref;if(pref==="auto"){var mq=window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)");resolved=mq&&mq.matches?"dark":"light";}document.documentElement.setAttribute(at,resolved);}catch(e){}})();`;
}

// src/branding-config.ts
function describeBrandingConfig(config) {
  const baseLineHeight = config.typography.lineHeights.normal ?? config.typography.lineHeights.tight ?? 1.5;
  return {
    id: config.id,
    version: config.version,
    aestheticDirection: config.aestheticDirection,
    paletteSize: Object.keys(config.colors.palette).length,
    chartSequenceLength: config.colors.chart.length,
    contrast: config.colors.contrastTargets,
    typography: {
      families: Object.keys(config.typography.families),
      scaleSteps: config.typography.scale.length,
      baseLineHeight,
      readingLineLength: {
        min: config.typography.readingLineLength.minCh,
        max: config.typography.readingLineLength.maxCh,
        target: config.typography.readingLineLength.targetCh
      },
      capsizeEnabled: config.typography.capsize.enabled
    },
    spacingScaleSteps: config.spacing.length,
    radiiKeys: Object.keys(config.radii),
    motionDurations: config.motion.durations,
    breakpointKeys: Object.keys(config.breakpoints).filter(
      (k) => k !== "container"
    ),
    a11y: {
      focusRingWidthPx: config.a11y.focusRingWidthPx,
      focusRingOffsetPx: config.a11y.focusRingOffsetPx,
      minTouchTargetPx: config.a11y.minTouchTargetPx,
      forcedColorsMode: config.a11y.forcedColors.mode
    },
    // Per cross-cutting design rule #6 — document failure modes / scope edges.
    excludes: [
      "copy/voice (future @norkron/voice lib)",
      "iconography (future @norkron/icons)",
      "illustration style",
      "sound design",
      "logo geometry"
    ]
  };
}

// src/css-emit.ts
var PREFIX = "--norkron";
function emitCssBlock(config, options = {}) {
  const selector = options.selector ?? ":root";
  const indent = options.indent ?? "	";
  const trailing = options.trailingNewline ?? true;
  const lines = [];
  lines.push(`${selector} {`);
  for (const [name, value] of emitVariablePairs(config)) {
    lines.push(`${indent}${name}: ${value};`);
  }
  lines.push("}");
  return lines.join("\n") + (trailing ? "\n" : "");
}
function emitVariablePairs(config) {
  const out = [];
  out.push([`${PREFIX}-brand-id`, JSON.stringify(config.id)]);
  out.push([`${PREFIX}-brand-version`, JSON.stringify(config.version)]);
  out.push([
    `${PREFIX}-brand-aesthetic`,
    JSON.stringify(config.aestheticDirection)
  ]);
  for (const key of Object.keys(config.colors.palette).sort()) {
    const tok = config.colors.palette[key];
    if (tok) out.push([`${PREFIX}-color-${kebab(key)}`, tok.value]);
  }
  for (const key of Object.keys(config.colors.surfaces).sort()) {
    const tok = config.colors.surfaces[key];
    if (tok) out.push([`${PREFIX}-surface-${kebab(key)}`, tok.value]);
  }
  for (const key of Object.keys(config.colors.text).sort()) {
    const tok = config.colors.text[key];
    if (tok) out.push([`${PREFIX}-text-${kebab(key)}`, tok.value]);
  }
  for (const key of Object.keys(config.colors.state).sort()) {
    const tok = config.colors.state[key];
    if (tok) out.push([`${PREFIX}-state-${kebab(key)}`, tok.value]);
  }
  config.colors.chart.forEach((tok, i) => {
    out.push([`${PREFIX}-chart-${i + 1}`, tok.value]);
  });
  out.push([
    `${PREFIX}-contrast-body-apca`,
    String(config.colors.contrastTargets.bodyAPCA)
  ]);
  out.push([
    `${PREFIX}-contrast-ui-apca`,
    String(config.colors.contrastTargets.uiAPCA)
  ]);
  for (const key of Object.keys(config.typography.families).sort()) {
    const stack = config.typography.families[key];
    if (stack) {
      out.push([
        `${PREFIX}-font-${kebab(key)}`,
        stack.stack.map(quoteIfNeeded).join(", ")
      ]);
    }
  }
  config.typography.scale.forEach((ratio, i) => {
    out.push([`${PREFIX}-type-scale-${i}`, `${ratio}rem`]);
  });
  for (const key of Object.keys(config.typography.lineHeights).sort()) {
    out.push([
      `${PREFIX}-line-height-${key}`,
      String(
        config.typography.lineHeights[key]
      )
    ]);
  }
  for (const key of Object.keys(config.typography.weights).sort()) {
    out.push([
      `${PREFIX}-font-weight-${kebab(key)}`,
      String(config.typography.weights[key])
    ]);
  }
  for (const key of Object.keys(config.typography.letterSpacing).sort()) {
    const v = config.typography.letterSpacing[key];
    if (v !== void 0) out.push([`${PREFIX}-letter-spacing-${kebab(key)}`, v]);
  }
  out.push([
    `${PREFIX}-reading-min-ch`,
    `${config.typography.readingLineLength.minCh}ch`
  ]);
  out.push([
    `${PREFIX}-reading-max-ch`,
    `${config.typography.readingLineLength.maxCh}ch`
  ]);
  out.push([
    `${PREFIX}-reading-target-ch`,
    `${config.typography.readingLineLength.targetCh}ch`
  ]);
  config.spacing.forEach((n, i) => {
    out.push([`${PREFIX}-space-${i}`, `${n}px`]);
  });
  for (const key of Object.keys(config.radii).sort()) {
    out.push([
      `${PREFIX}-radius-${kebab(key)}`,
      config.radii[key] ?? "0"
    ]);
  }
  for (const key of Object.keys(config.motion.durations).sort()) {
    const ms = config.motion.durations[key];
    out.push([`${PREFIX}-duration-${kebab(key)}`, `${ms}ms`]);
  }
  for (const key of Object.keys(config.motion.easings).sort()) {
    const v = config.motion.easings[key];
    if (v !== void 0) out.push([`${PREFIX}-easing-${kebab(key)}`, v]);
  }
  const bp = config.breakpoints;
  out.push([`${PREFIX}-bp-sm`, `${bp.sm}px`]);
  out.push([`${PREFIX}-bp-md`, `${bp.md}px`]);
  out.push([`${PREFIX}-bp-lg`, `${bp.lg}px`]);
  out.push([`${PREFIX}-bp-xl`, `${bp.xl}px`]);
  out.push([`${PREFIX}-bp-2xl`, `${bp["2xl"]}px`]);
  for (const key of Object.keys(bp.container).sort()) {
    const v = bp.container[key];
    if (v !== void 0)
      out.push([`${PREFIX}-container-${kebab(key)}`, `${v}px`]);
  }
  out.push([
    `${PREFIX}-focus-ring-width`,
    `${config.a11y.focusRingWidthPx}px`
  ]);
  out.push([
    `${PREFIX}-focus-ring-offset`,
    `${config.a11y.focusRingOffsetPx}px`
  ]);
  const stateColors = config.colors.state;
  const focusRingColor = config.a11y.focusRingColor ?? stateColors.focus?.value ?? stateColors.info?.value ?? stateColors.neutral?.value ?? "#0969da";
  out.push([`${PREFIX}-focus-ring-color`, focusRingColor]);
  out.push([
    `${PREFIX}-touch-target-min`,
    `${config.a11y.minTouchTargetPx}px`
  ]);
  return out;
}
function kebab(s) {
  return s.replace(/([a-z0-9])([A-Z])/g, "$1-$2").replace(/[\s_]+/g, "-").toLowerCase();
}
function quoteIfNeeded(family) {
  if (/^[A-Za-z][A-Za-z0-9-]*$/.test(family)) return family;
  if (family.startsWith('"') || family.startsWith("'")) return family;
  return `"${family.replace(/"/g, '\\"')}"`;
}
function applyBrandingConfig(config) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const pairs = emitVariablePairs(config);
  for (const [name, value] of pairs) {
    root.style.setProperty(name, value);
  }
}

// src/presets/_shared.ts
var baseSpacing = [4, 8, 12, 16, 24, 32, 48, 64, 96];
var baseBreakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
  container: { narrow: 360, base: 720, wide: 1080 }
};
var baseA11y = {
  focusRingWidthPx: 2,
  focusRingOffsetPx: 2,
  minTouchTargetPx: 44,
  forcedColors: {
    mode: "override",
    tokenMap: {
      "--norkron-text-primary": "CanvasText",
      "--norkron-surface-base": "Canvas",
      "--norkron-state-danger": "Mark"
    }
  },
  prefersReducedTransparency: "opaque"
};
var baseMotion = {
  durations: {
    instant: 0,
    fast: 120,
    base: 200,
    slow: 320,
    slowest: 500
  },
  easings: {
    standard: "cubic-bezier(0.2, 0, 0, 1)",
    emphasized: "cubic-bezier(0.3, 0, 0, 1)",
    decelerate: "cubic-bezier(0, 0, 0, 1)",
    accelerate: "cubic-bezier(0.3, 0, 1, 1)"
  },
  reducedMotionStrategy: "shorten"
};
var baseRadii = {
  none: "0",
  sm: "2px",
  md: "4px",
  lg: "8px",
  pill: "9999px"
};
var baseTypography = {
  families: {
    sans: { stack: ["Inter", "system-ui", "sans-serif"], display: "swap" },
    serif: { stack: ["Georgia", "serif"] },
    mono: { stack: ["ui-monospace", "monospace"] },
    display: { stack: ["Inter", "system-ui", "sans-serif"] }
  },
  scale: [0.75, 0.875, 1, 1.125, 1.25, 1.5, 2, 2.5, 3.5],
  lineHeights: { tight: 1.2, normal: 1.5, loose: 1.75 },
  weights: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  letterSpacing: { tight: "-0.01em", normal: "0", wide: "0.04em" },
  readingLineLength: { minCh: 45, maxCh: 75, targetCh: 65 },
  capsize: { enabled: false, capHeightTrim: "auto" }
};

// src/presets/editorial.ts
var editorial = {
  id: "editorial",
  version: "1.0.0",
  aestheticDirection: "editorial",
  colors: {
    palette: {
      ink: { value: "#1a1a1a", description: "Body ink" },
      paper: { value: "#fbfaf6", description: "Paper background" },
      rule: { value: "#d4cfc1", description: "Hairline rule" },
      accent: { value: "#7a2e1f", description: "Editorial red" }
    },
    surfaces: {
      base: { value: "#fbfaf6" },
      elevated: { value: "#ffffff" },
      sunken: { value: "#f0ecdf" },
      overlay: { value: "rgba(26,26,26,0.55)" },
      inverse: { value: "#1a1a1a" }
    },
    text: {
      primary: { value: "#1a1a1a" },
      secondary: { value: "#3d3d3d" },
      muted: { value: "#7a7368" },
      disabled: { value: "#bdb9ab" },
      inverse: { value: "#fbfaf6" },
      link: { value: "#7a2e1f" }
    },
    state: {
      info: { value: "#26506e" },
      success: { value: "#3e6b2a" },
      warn: { value: "#8c6a1f" },
      danger: { value: "#7a2e1f" },
      neutral: { value: "#3d3d3d" }
    },
    chart: [
      { value: "#7a2e1f" },
      { value: "#26506e" },
      { value: "#8c6a1f" },
      { value: "#3e6b2a" },
      { value: "#5b3b76" },
      { value: "#3d3d3d" }
    ],
    contrastTargets: { bodyAPCA: 80, uiAPCA: 65, enforce: "APCA" }
  },
  typography: {
    ...baseTypography,
    families: {
      sans: { stack: ["Inter", "system-ui", "sans-serif"], display: "swap" },
      serif: {
        stack: ['"Source Serif Pro"', "Georgia", "serif"],
        display: "swap"
      },
      mono: { stack: ["ui-monospace", "monospace"] },
      display: {
        stack: ['"Source Serif Pro"', "Georgia", "serif"],
        display: "swap"
      }
    },
    scale: [0.75, 0.875, 1, 1.125, 1.333, 1.777, 2.369, 3.157, 4.209],
    readingLineLength: { minCh: 55, maxCh: 80, targetCh: 70 }
  },
  spacing: baseSpacing,
  radii: { ...baseRadii, md: "2px", lg: "4px" },
  motion: baseMotion,
  breakpoints: baseBreakpoints,
  a11y: baseA11y
};

// src/presets/brutalist.ts
var brutalist = {
  id: "brutalist",
  version: "1.0.0",
  aestheticDirection: "brutalist",
  colors: {
    palette: {
      black: { value: "#000000" },
      white: { value: "#ffffff" },
      alert: { value: "#ff0000" },
      focus: { value: "#0000ff" }
    },
    surfaces: {
      base: { value: "#ffffff" },
      elevated: { value: "#ffffff" },
      sunken: { value: "#f4f4f4" },
      overlay: { value: "rgba(0,0,0,0.85)" },
      inverse: { value: "#000000" }
    },
    text: {
      primary: { value: "#000000" },
      secondary: { value: "#000000" },
      muted: { value: "#555555" },
      disabled: { value: "#999999" },
      inverse: { value: "#ffffff" },
      link: { value: "#0000ff" }
    },
    state: {
      info: { value: "#0000ff" },
      success: { value: "#008000" },
      warn: { value: "#ff8c00" },
      danger: { value: "#ff0000" },
      neutral: { value: "#000000" }
    },
    chart: [
      { value: "#000000" },
      { value: "#ff0000" },
      { value: "#0000ff" },
      { value: "#008000" },
      { value: "#ff8c00" },
      { value: "#800080" }
    ],
    contrastTargets: { bodyAPCA: 90, uiAPCA: 75, enforce: "both" }
  },
  typography: {
    ...baseTypography,
    families: {
      sans: {
        stack: ["ui-monospace", '"SF Mono"', "Menlo", "monospace"]
      },
      serif: { stack: ["Courier", "monospace"] },
      mono: {
        stack: ["ui-monospace", '"SF Mono"', "Menlo", "monospace"]
      },
      display: {
        stack: ["ui-monospace", '"SF Mono"', "Menlo", "monospace"]
      }
    },
    scale: [0.875, 1, 1, 1.125, 1.25, 1.5, 2, 2.5, 3],
    lineHeights: { tight: 1, normal: 1.25, loose: 1.5 },
    weights: { regular: 400, medium: 400, semibold: 700, bold: 700 }
  },
  spacing: baseSpacing,
  radii: { none: "0", sm: "0", md: "0", lg: "0", pill: "0" },
  motion: { ...baseMotion, reducedMotionStrategy: "remove" },
  breakpoints: baseBreakpoints,
  a11y: { ...baseA11y, focusRingWidthPx: 3, focusRingOffsetPx: 0 }
};

// src/presets/dense-data.ts
var denseData = {
  id: "dense-data",
  version: "1.0.0",
  aestheticDirection: "dense-data",
  colors: {
    palette: {
      bg: { value: "#0d1117" },
      fg: { value: "#c9d1d9" },
      grid: { value: "#21262d" },
      accent: { value: "#58a6ff" }
    },
    surfaces: {
      base: { value: "#0d1117" },
      elevated: { value: "#161b22" },
      sunken: { value: "#010409" },
      overlay: { value: "rgba(0,0,0,0.7)" },
      inverse: { value: "#f6f8fa" }
    },
    text: {
      primary: { value: "#c9d1d9" },
      secondary: { value: "#8b949e" },
      muted: { value: "#6e7681" },
      disabled: { value: "#484f58" },
      inverse: { value: "#0d1117" },
      link: { value: "#58a6ff" }
    },
    state: {
      info: { value: "#58a6ff" },
      success: { value: "#3fb950" },
      warn: { value: "#d29922" },
      danger: { value: "#f85149" },
      neutral: { value: "#8b949e" }
    },
    chart: [
      { value: "#58a6ff" },
      { value: "#3fb950" },
      { value: "#d29922" },
      { value: "#f85149" },
      { value: "#bc8cff" },
      { value: "#ff7b72" },
      { value: "#79c0ff" },
      { value: "#ffa657" }
    ],
    contrastTargets: { bodyAPCA: 75, uiAPCA: 60, enforce: "APCA" }
  },
  typography: {
    ...baseTypography,
    families: {
      sans: { stack: ["Inter", "system-ui", "sans-serif"] },
      serif: { stack: ["Georgia", "serif"] },
      mono: {
        stack: ['"Berkeley Mono"', "ui-monospace", '"JetBrains Mono"', "monospace"]
      },
      display: { stack: ["Inter", "system-ui", "sans-serif"] }
    },
    scale: [0.6875, 0.75, 0.8125, 0.875, 1, 1.125, 1.25, 1.5, 2],
    lineHeights: { tight: 1.15, normal: 1.35, loose: 1.55 },
    readingLineLength: { minCh: 60, maxCh: 120, targetCh: 90 }
  },
  spacing: [2, 4, 6, 8, 12, 16, 24, 32, 48],
  radii: { none: "0", sm: "1px", md: "2px", lg: "3px", pill: "9999px" },
  motion: { ...baseMotion, durations: { instant: 0, fast: 80, base: 120, slow: 200, slowest: 320 } },
  breakpoints: baseBreakpoints,
  a11y: baseA11y
};

// src/presets/playful-native.ts
var playfulNative = {
  id: "playful-native",
  version: "1.0.0",
  aestheticDirection: "playful-native",
  colors: {
    palette: {
      sky: { value: "#4ec3ff" },
      peach: { value: "#ffb085" },
      mint: { value: "#7fe3c4" },
      lilac: { value: "#c7a8ff" },
      sand: { value: "#fff4e6" }
    },
    surfaces: {
      base: { value: "#fff8f0" },
      elevated: { value: "#ffffff" },
      sunken: { value: "#fde9d9" },
      overlay: { value: "rgba(80,40,30,0.45)" },
      inverse: { value: "#2b1e15" }
    },
    text: {
      primary: { value: "#2b1e15" },
      secondary: { value: "#5b463a" },
      muted: { value: "#8d7466" },
      disabled: { value: "#c4b3a8" },
      inverse: { value: "#fff8f0" },
      link: { value: "#1f80b8" }
    },
    state: {
      info: { value: "#4ec3ff" },
      success: { value: "#3fb98b" },
      warn: { value: "#ffb085" },
      danger: { value: "#ff6b6b" },
      neutral: { value: "#8d7466" }
    },
    chart: [
      { value: "#4ec3ff" },
      { value: "#ffb085" },
      { value: "#7fe3c4" },
      { value: "#c7a8ff" },
      { value: "#ffd76b" },
      { value: "#ff6b9d" }
    ],
    contrastTargets: { bodyAPCA: 75, uiAPCA: 60, enforce: "APCA" }
  },
  typography: {
    ...baseTypography,
    families: {
      sans: { stack: ['"SF Pro Rounded"', "system-ui", "sans-serif"] },
      serif: { stack: ['"Source Serif Pro"', "Georgia", "serif"] },
      mono: { stack: ["ui-monospace", "monospace"] },
      display: { stack: ['"SF Pro Rounded"', "system-ui", "sans-serif"] }
    },
    scale: [0.75, 0.875, 1, 1.125, 1.25, 1.5, 2, 2.5, 3],
    lineHeights: { tight: 1.3, normal: 1.55, loose: 1.8 }
  },
  spacing: baseSpacing,
  radii: { none: "0", sm: "6px", md: "12px", lg: "20px", pill: "9999px" },
  motion: {
    durations: { instant: 0, fast: 180, base: 280, slow: 420, slowest: 600 },
    easings: {
      standard: "cubic-bezier(0.34, 1.56, 0.64, 1)",
      emphasized: "cubic-bezier(0.5, 1.6, 0.5, 1)",
      decelerate: "cubic-bezier(0, 0, 0, 1)",
      accelerate: "cubic-bezier(0.5, 0, 1, 1)"
    },
    reducedMotionStrategy: "replace-with-fade"
  },
  breakpoints: baseBreakpoints,
  a11y: { ...baseA11y, focusRingWidthPx: 3, focusRingOffsetPx: 3 }
};

// src/presets/premium-restrained.ts
var premiumRestrained = {
  id: "premium-restrained",
  version: "1.0.0",
  aestheticDirection: "premium-restrained",
  colors: {
    palette: {
      noir: { value: "#0b0b0b" },
      pearl: { value: "#f4f1ec" },
      gold: { value: "#a47a32" },
      graphite: { value: "#2c2c2c" }
    },
    surfaces: {
      base: { value: "#f4f1ec" },
      elevated: { value: "#ffffff" },
      sunken: { value: "#e7e2da" },
      overlay: { value: "rgba(11,11,11,0.6)" },
      inverse: { value: "#0b0b0b" }
    },
    text: {
      primary: { value: "#0b0b0b" },
      secondary: { value: "#2c2c2c" },
      muted: { value: "#7a7368" },
      disabled: { value: "#b5afa4" },
      inverse: { value: "#f4f1ec" },
      link: { value: "#a47a32" }
    },
    state: {
      info: { value: "#3d4a5c" },
      success: { value: "#4a5d3e" },
      warn: { value: "#a47a32" },
      danger: { value: "#7a2e1f" },
      neutral: { value: "#2c2c2c" }
    },
    chart: [
      { value: "#0b0b0b" },
      { value: "#2c2c2c" },
      { value: "#7a7368" },
      { value: "#a47a32" },
      { value: "#3d4a5c" },
      { value: "#7a2e1f" }
    ],
    contrastTargets: { bodyAPCA: 85, uiAPCA: 70, enforce: "APCA" }
  },
  typography: {
    ...baseTypography,
    families: {
      sans: { stack: ['"Neue Haas Grotesk"', "Inter", "system-ui", "sans-serif"] },
      serif: { stack: ['"GT Sectra"', "Georgia", "serif"] },
      mono: { stack: ["ui-monospace", "monospace"] },
      display: { stack: ['"GT Sectra"', "Georgia", "serif"] }
    },
    scale: [0.75, 0.875, 1, 1.125, 1.25, 1.5, 2, 2.75, 3.75],
    lineHeights: { tight: 1.15, normal: 1.55, loose: 1.8 },
    letterSpacing: { tight: "-0.02em", normal: "0", wide: "0.08em" },
    readingLineLength: { minCh: 55, maxCh: 75, targetCh: 65 }
  },
  spacing: baseSpacing,
  radii: { none: "0", sm: "1px", md: "2px", lg: "3px", pill: "9999px" },
  motion: {
    durations: { instant: 0, fast: 140, base: 240, slow: 400, slowest: 640 },
    easings: {
      standard: "cubic-bezier(0.22, 0, 0.18, 1)",
      emphasized: "cubic-bezier(0.25, 0, 0.1, 1)",
      decelerate: "cubic-bezier(0, 0, 0.18, 1)",
      accelerate: "cubic-bezier(0.4, 0, 1, 1)"
    },
    reducedMotionStrategy: "shorten"
  },
  breakpoints: baseBreakpoints,
  a11y: baseA11y
};

// src/presets/geological.ts
var geological = {
  id: "geological",
  version: "1.0.0",
  aestheticDirection: "geological",
  colors: {
    palette: {
      basalt: { value: "#1f2222", description: "Basalt \u2014 deepest neutral" },
      sediment: { value: "#5a564e", description: "Sediment mid-neutral" },
      chalk: { value: "#f3efe6", description: "Chalk surface" },
      copper: { value: "#a85a2d", description: "Copper \u2014 primary accent" },
      peridot: { value: "#7a8c3a", description: "Peridot \u2014 success accent" },
      ochre: { value: "#b89232", description: "Ochre \u2014 warn accent" }
    },
    surfaces: {
      base: { value: "#f3efe6" },
      elevated: { value: "#fbf8f1" },
      sunken: { value: "#e6e1d4" },
      overlay: { value: "rgba(31,34,34,0.62)" },
      inverse: { value: "#1f2222" }
    },
    text: {
      primary: { value: "#1f2222" },
      secondary: { value: "#3a3a37" },
      muted: { value: "#5a564e" },
      disabled: { value: "#aaa49a" },
      inverse: { value: "#f3efe6" },
      link: { value: "#a85a2d" }
    },
    state: {
      info: { value: "#3d5a6e" },
      success: { value: "#7a8c3a" },
      warn: { value: "#b89232" },
      danger: { value: "#a85a2d" },
      neutral: { value: "#5a564e" }
    },
    chart: [
      { value: "#a85a2d" },
      { value: "#7a8c3a" },
      { value: "#b89232" },
      { value: "#3d5a6e" },
      { value: "#5a564e" },
      { value: "#1f2222" }
    ],
    contrastTargets: { bodyAPCA: 80, uiAPCA: 65, enforce: "APCA" }
  },
  typography: {
    families: {
      sans: { stack: ["Inter", "system-ui", "sans-serif"], display: "swap" },
      serif: { stack: ['"Source Serif Pro"', "Georgia", "serif"] },
      mono: {
        stack: ['"Berkeley Mono"', "ui-monospace", '"JetBrains Mono"', "monospace"],
        display: "swap"
      },
      display: { stack: ["Inter", "system-ui", "sans-serif"] }
    },
    scale: [0.75, 0.875, 1, 1.125, 1.25, 1.5, 2, 2.5, 3.25],
    lineHeights: { tight: 1.2, normal: 1.5, loose: 1.75 },
    weights: { regular: 400, medium: 500, semibold: 600, bold: 700 },
    letterSpacing: { tight: "-0.01em", normal: "0", wide: "0.06em" },
    readingLineLength: { minCh: 50, maxCh: 75, targetCh: 65 },
    capsize: { enabled: true, capHeightTrim: "auto" }
  },
  spacing: baseSpacing,
  // 0px radii — stratum lines, no rounded corners.
  radii: { none: "0", sm: "0", md: "0", lg: "0", pill: "9999px" },
  motion: {
    durations: { instant: 0, fast: 140, base: 240, slow: 420, slowest: 700 },
    // Asymmetric — slow-in, fast-out: geological settling.
    easings: {
      standard: "cubic-bezier(0.65, 0, 0.18, 1)",
      emphasized: "cubic-bezier(0.75, 0, 0.15, 1)",
      decelerate: "cubic-bezier(0, 0, 0.18, 1)",
      accelerate: "cubic-bezier(0.6, 0, 1, 1)"
    },
    reducedMotionStrategy: "shorten"
  },
  breakpoints: baseBreakpoints,
  a11y: baseA11y
};

// src/presets/index.ts
var PRESETS = {
  editorial,
  brutalist,
  "dense-data": denseData,
  "playful-native": playfulNative,
  "premium-restrained": premiumRestrained,
  geological
};
function getPreset(name) {
  return PRESETS[name];
}

// src/learn.ts
function normaliseColor(s) {
  return s.trim().toLowerCase().replace(/\s+/g, "");
}
function topColors(freq, n) {
  return [...freq.entries()].filter(([k]) => k && k !== "rgba(0,0,0,0)" && k !== "transparent").sort((a, b) => b[1] - a[1]).slice(0, n).map(([value, count]) => ({ value, count }));
}
function detectAesthetic(radii, families) {
  const hasMono = [...families].some(
    (f) => /mono|courier|berkeley|menlo|consolas/i.test(f)
  );
  const maxRadius = Math.max(0, ...radii);
  if (hasMono && maxRadius === 0) return "brutalist";
  if (hasMono) return "dense-data";
  if (maxRadius >= 16) return "playful-native";
  if (maxRadius <= 2) return "editorial";
  return "geological";
}
function learnBrandingConfig(root, options = {}) {
  const seed = options.seed ?? geological;
  const notes = [];
  if (typeof window === "undefined" || typeof document === "undefined" || typeof getComputedStyle === "undefined") {
    notes.push("No DOM environment detected; returning seed config unchanged.");
    return {
      config: { ...seed, id: options.suggestedId ?? "learned-from-dom" },
      notes,
      suggestedAesthetic: seed.aestheticDirection
    };
  }
  const target = root ?? document.body;
  if (!target) {
    notes.push("No root element supplied and document.body unavailable.");
    return {
      config: { ...seed, id: options.suggestedId ?? "learned-from-dom" },
      notes,
      suggestedAesthetic: seed.aestheticDirection
    };
  }
  const limit = options.sampleLimit ?? 500;
  const all = target.querySelectorAll("*");
  const sampleStep = Math.max(1, Math.floor(all.length / limit));
  const bgFreq = /* @__PURE__ */ new Map();
  const fgFreq = /* @__PURE__ */ new Map();
  const families = /* @__PURE__ */ new Set();
  const radii = [];
  const fontSizes = /* @__PURE__ */ new Set();
  for (let i = 0; i < all.length; i += sampleStep) {
    const el = all[i];
    if (!el || !(el instanceof Element)) continue;
    const cs = getComputedStyle(el);
    const bg = normaliseColor(cs.backgroundColor);
    const fg = normaliseColor(cs.color);
    bgFreq.set(bg, (bgFreq.get(bg) ?? 0) + 1);
    fgFreq.set(fg, (fgFreq.get(fg) ?? 0) + 1);
    const fam = cs.fontFamily.split(",")[0]?.trim().replace(/['"]/g, "");
    if (fam) families.add(fam);
    const radius = parseFloat(cs.borderTopLeftRadius || "0");
    if (Number.isFinite(radius)) radii.push(radius);
    const fs = parseFloat(cs.fontSize || "0");
    if (Number.isFinite(fs) && fs > 0) fontSizes.add(Math.round(fs));
  }
  notes.push(`Sampled ${Math.ceil(all.length / sampleStep)} elements`);
  const topBgs = topColors(bgFreq, 6);
  const topFgs = topColors(fgFreq, 4);
  const aesthetic = detectAesthetic(radii, families);
  notes.push(`Detected aesthetic: ${aesthetic} (max radius ${Math.max(0, ...radii)}px)`);
  const palette = {};
  topBgs.forEach((c, i) => {
    palette[`bg-${i + 1}`] = { value: c.value, description: `Detected bg #${i + 1}` };
  });
  topFgs.forEach((c, i) => {
    palette[`fg-${i + 1}`] = { value: c.value, description: `Detected fg #${i + 1}` };
  });
  const config = {
    ...seed,
    id: options.suggestedId ?? "learned-from-dom",
    aestheticDirection: aesthetic,
    colors: {
      ...seed.colors,
      palette: { ...seed.colors.palette, ...palette },
      text: topFgs[0] ? { ...seed.colors.text, primary: { value: topFgs[0].value } } : seed.colors.text,
      surfaces: topBgs[0] ? { ...seed.colors.surfaces, base: { value: topBgs[0].value } } : seed.colors.surfaces
    },
    typography: families.size > 0 ? {
      ...seed.typography,
      families: {
        ...seed.typography.families,
        sans: {
          stack: [...families].slice(0, 3)
        }
      }
    } : seed.typography
  };
  return { config, notes, suggestedAesthetic: aesthetic };
}

// src/breakpoints.ts
var CANONICAL_BREAKPOINTS = {
  xs: 0,
  // mobile-narrow (default, no min-width needed)
  sm: 480,
  // mobile
  md: 768,
  // tablet
  lg: 1024,
  // laptop
  xl: 1280,
  // desktop
  "2xl": 1536,
  // wide-desktop
  "3xl": 1920
  // ultra-wide
};
var BREAKPOINT_ORDER = [
  "xs",
  "sm",
  "md",
  "lg",
  "xl",
  "2xl",
  "3xl"
];
var CANONICAL_BREAKPOINT_PX = BREAKPOINT_ORDER.map(
  (bp) => CANONICAL_BREAKPOINTS[bp]
);
function matchesBreakpoint(viewport, bp) {
  if (!Number.isFinite(viewport) || viewport < 0) return false;
  return viewport >= CANONICAL_BREAKPOINTS[bp];
}
function currentBreakpoint(viewport) {
  const vw = viewport ?? (typeof globalThis !== "undefined" && typeof globalThis.innerWidth === "number" ? globalThis.innerWidth : 0);
  let current = "xs";
  for (const bp of BREAKPOINT_ORDER) {
    if (vw >= CANONICAL_BREAKPOINTS[bp]) current = bp;
    else break;
  }
  return current;
}
function nextBreakpointUp(bp) {
  const idx = BREAKPOINT_ORDER.indexOf(bp);
  if (idx < 0 || idx === BREAKPOINT_ORDER.length - 1) return null;
  return BREAKPOINT_ORDER[idx + 1] ?? null;
}
function isCanonicalBreakpointPx(px) {
  return CANONICAL_BREAKPOINT_PX.includes(px);
}
function nearestCanonicalBreakpoint(px) {
  let best = "xs";
  let bestDist = Math.abs(px - CANONICAL_BREAKPOINTS.xs);
  for (const bp of BREAKPOINT_ORDER) {
    const dist = Math.abs(px - CANONICAL_BREAKPOINTS[bp]);
    if (dist < bestDist) {
      best = bp;
      bestDist = dist;
    }
  }
  return {
    breakpoint: best,
    px: CANONICAL_BREAKPOINTS[best],
    distance: bestDist
  };
}
var A_LIGHT_SURFACE_BASE = "#ffffff";
var A_LIGHT_SURFACE_ELEVATED = "#fafafa";
var A_LIGHT_SURFACE_SUNKEN = "#f4f4f4";
var A_LIGHT_TEXT_PRIMARY = "#111111";
var A_LIGHT_TEXT_SECONDARY = "#3d3d3d";
var A_LIGHT_TEXT_MUTED = "#595959";
var A_LIGHT_TEXT_DISABLED = "#9a9a9a";
var A_LIGHT_BORDER_SUBTLE = "#e0e0e0";
var A_LIGHT_BORDER_STRONG = "#3d3d3d";
var A_LIGHT_ACCENT = "#1a1a1a";
var A_LIGHT_DANGER = "#a10000";
var A_LIGHT_WARNING = "#7a4d00";
var A_LIGHT_SUCCESS = "#1e5e1e";
var A_DARK_SURFACE_BASE = "#0a0a0a";
var A_DARK_SURFACE_ELEVATED = "#161616";
var A_DARK_SURFACE_SUNKEN = "#050505";
var A_DARK_TEXT_PRIMARY = "#f5f5f5";
var A_DARK_TEXT_SECONDARY = "#cccccc";
var A_DARK_TEXT_MUTED = "#a3a3a3";
var A_DARK_TEXT_DISABLED = "#6a6a6a";
var A_DARK_BORDER_SUBTLE = "#262626";
var A_DARK_BORDER_STRONG = "#cccccc";
var A_DARK_ACCENT = "#f5f5f5";
var A_DARK_DANGER = "#ff8a8a";
var A_DARK_WARNING = "#ffc107";
var A_DARK_SUCCESS = "#7fd17f";
var B_LIGHT_SURFACE_BASE = "#ffffff";
var B_LIGHT_SURFACE_ELEVATED = "#f8fafc";
var B_LIGHT_SURFACE_SUNKEN = "#f1f5f9";
var B_LIGHT_TEXT_PRIMARY = "#0f172a";
var B_LIGHT_TEXT_SECONDARY = "#475569";
var B_LIGHT_TEXT_MUTED = "#52617a";
var B_LIGHT_TEXT_DISABLED = "#94a3b8";
var B_LIGHT_BORDER_SUBTLE = "#cbd5e1";
var B_LIGHT_BORDER_STRONG = "#475569";
var B_LIGHT_ACCENT = "#1d4ed8";
var B_LIGHT_DANGER = "#b91c1c";
var B_LIGHT_WARNING = "#92400e";
var B_LIGHT_SUCCESS = "#047857";
var B_DARK_SURFACE_BASE = "#0b1220";
var B_DARK_SURFACE_ELEVATED = "#1e293b";
var B_DARK_SURFACE_SUNKEN = "#020617";
var B_DARK_TEXT_PRIMARY = "#f8fafc";
var B_DARK_TEXT_SECONDARY = "#cbd5e1";
var B_DARK_TEXT_MUTED = "#94a3b8";
var B_DARK_TEXT_DISABLED = "#64748b";
var B_DARK_BORDER_SUBTLE = "#334155";
var B_DARK_BORDER_STRONG = "#94a3b8";
var B_DARK_ACCENT = "#60a5fa";
var B_DARK_DANGER = "#fca5a5";
var B_DARK_WARNING = "#fbbf24";
var B_DARK_SUCCESS = "#34d399";
var C_LIGHT_SURFACE_BASE = "#fdfaf6";
var C_LIGHT_SURFACE_ELEVATED = "#fffefa";
var C_LIGHT_SURFACE_SUNKEN = "#f5ecdf";
var C_LIGHT_TEXT_PRIMARY = "#1a1207";
var C_LIGHT_TEXT_SECONDARY = "#4a3520";
var C_LIGHT_TEXT_MUTED = "#6b4f30";
var C_LIGHT_TEXT_DISABLED = "#a89478";
var C_LIGHT_BORDER_SUBTLE = "#d4c3a8";
var C_LIGHT_BORDER_STRONG = "#5a3d1a";
var C_LIGHT_ACCENT = "#7a1f4a";
var C_LIGHT_DANGER = "#8a1414";
var C_LIGHT_WARNING = "#7a4a00";
var C_LIGHT_SUCCESS = "#1f5a1f";
var C_DARK_SURFACE_BASE = "#1a0e1f";
var C_DARK_SURFACE_ELEVATED = "#2a1a30";
var C_DARK_SURFACE_SUNKEN = "#0d0511";
var C_DARK_TEXT_PRIMARY = "#fdf3f9";
var C_DARK_TEXT_SECONDARY = "#e0c4d8";
var C_DARK_TEXT_MUTED = "#b890ad";
var C_DARK_TEXT_DISABLED = "#7a607a";
var C_DARK_BORDER_SUBTLE = "#3a2540";
var C_DARK_BORDER_STRONG = "#e0c4d8";
var C_DARK_ACCENT = "#f5b8d4";
var C_DARK_DANGER = "#ffa0a0";
var C_DARK_WARNING = "#ffd870";
var C_DARK_SUCCESS = "#9bdb9b";
var C_LIGHT_DECOR_GRADIENT = "linear-gradient(135deg, #fdfaf6 0%, #f5e6d3 50%, #fdfaf6 100%)";
var C_DARK_DECOR_GRADIENT = "linear-gradient(135deg, #1a0e1f 0%, #2a1530 50%, #1a0e1f 100%)";
var C_LIGHT_SHADOW_ELEVATION_3 = "0 12px 32px rgba(122,31,74,0.18), 0 4px 8px rgba(0,0,0,0.08)";
var C_DARK_SHADOW_ELEVATION_3 = "0 12px 32px rgba(245,184,212,0.20), 0 4px 8px rgba(0,0,0,0.5)";
function buildBranding(p) {
  return {
    ...editorial,
    id: p.id,
    version: "1.0.0",
    aestheticDirection: "editorial",
    colors: {
      palette: {
        "border-subtle": { value: p.borderSubtle },
        "border-strong": { value: p.borderStrong },
        accent: { value: p.accent }
      },
      surfaces: {
        base: { value: p.surfaceBase },
        elevated: { value: p.surfaceElevated },
        sunken: { value: p.surfaceSunken },
        overlay: { value: p.overlay },
        inverse: { value: p.inverse }
      },
      text: {
        primary: { value: p.textPrimary },
        secondary: { value: p.textSecondary },
        muted: { value: p.textMuted },
        disabled: { value: p.textDisabled },
        inverse: { value: p.textInverse },
        link: { value: p.link }
      },
      state: {
        info: { value: p.accent },
        success: { value: p.success },
        warn: { value: p.warning },
        danger: { value: p.danger },
        neutral: { value: p.textSecondary }
      },
      chart: [
        { value: p.accent },
        { value: p.success },
        { value: p.warning },
        { value: p.danger }
      ],
      contrastTargets: { bodyAPCA: 75, uiAPCA: 60, enforce: "both" }
    }
  };
}
var minimalisticCompactLight = buildBranding({
  id: "norkron/canonical-variant-a-minimalistic-compact-light",
  surfaceBase: A_LIGHT_SURFACE_BASE,
  surfaceElevated: A_LIGHT_SURFACE_ELEVATED,
  surfaceSunken: A_LIGHT_SURFACE_SUNKEN,
  overlay: "rgba(17,17,17,0.5)",
  inverse: A_DARK_SURFACE_BASE,
  textPrimary: A_LIGHT_TEXT_PRIMARY,
  textSecondary: A_LIGHT_TEXT_SECONDARY,
  textMuted: A_LIGHT_TEXT_MUTED,
  textDisabled: A_LIGHT_TEXT_DISABLED,
  textInverse: A_DARK_TEXT_PRIMARY,
  link: A_LIGHT_ACCENT,
  borderSubtle: A_LIGHT_BORDER_SUBTLE,
  borderStrong: A_LIGHT_BORDER_STRONG,
  accent: A_LIGHT_ACCENT,
  danger: A_LIGHT_DANGER,
  warning: A_LIGHT_WARNING,
  success: A_LIGHT_SUCCESS
});
var minimalisticCompactDark = buildBranding({
  id: "norkron/canonical-variant-a-minimalistic-compact-dark",
  surfaceBase: A_DARK_SURFACE_BASE,
  surfaceElevated: A_DARK_SURFACE_ELEVATED,
  surfaceSunken: A_DARK_SURFACE_SUNKEN,
  overlay: "rgba(5,5,5,0.7)",
  inverse: A_LIGHT_SURFACE_BASE,
  textPrimary: A_DARK_TEXT_PRIMARY,
  textSecondary: A_DARK_TEXT_SECONDARY,
  textMuted: A_DARK_TEXT_MUTED,
  textDisabled: A_DARK_TEXT_DISABLED,
  textInverse: A_LIGHT_TEXT_PRIMARY,
  link: A_DARK_ACCENT,
  borderSubtle: A_DARK_BORDER_SUBTLE,
  borderStrong: A_DARK_BORDER_STRONG,
  accent: A_DARK_ACCENT,
  danger: A_DARK_DANGER,
  warning: A_DARK_WARNING,
  success: A_DARK_SUCCESS
});
var modernAverageLight = buildBranding({
  id: "norkron/canonical-variant-b-modern-average-light",
  surfaceBase: B_LIGHT_SURFACE_BASE,
  surfaceElevated: B_LIGHT_SURFACE_ELEVATED,
  surfaceSunken: B_LIGHT_SURFACE_SUNKEN,
  overlay: "rgba(15,23,42,0.5)",
  inverse: B_DARK_SURFACE_BASE,
  textPrimary: B_LIGHT_TEXT_PRIMARY,
  textSecondary: B_LIGHT_TEXT_SECONDARY,
  textMuted: B_LIGHT_TEXT_MUTED,
  textDisabled: B_LIGHT_TEXT_DISABLED,
  textInverse: B_DARK_TEXT_PRIMARY,
  link: B_LIGHT_ACCENT,
  borderSubtle: B_LIGHT_BORDER_SUBTLE,
  borderStrong: B_LIGHT_BORDER_STRONG,
  accent: B_LIGHT_ACCENT,
  danger: B_LIGHT_DANGER,
  warning: B_LIGHT_WARNING,
  success: B_LIGHT_SUCCESS
});
var modernAverageDark = buildBranding({
  id: "norkron/canonical-variant-b-modern-average-dark",
  surfaceBase: B_DARK_SURFACE_BASE,
  surfaceElevated: B_DARK_SURFACE_ELEVATED,
  surfaceSunken: B_DARK_SURFACE_SUNKEN,
  overlay: "rgba(2,6,23,0.7)",
  inverse: B_LIGHT_SURFACE_BASE,
  textPrimary: B_DARK_TEXT_PRIMARY,
  textSecondary: B_DARK_TEXT_SECONDARY,
  textMuted: B_DARK_TEXT_MUTED,
  textDisabled: B_DARK_TEXT_DISABLED,
  textInverse: B_LIGHT_TEXT_PRIMARY,
  link: B_DARK_ACCENT,
  borderSubtle: B_DARK_BORDER_SUBTLE,
  borderStrong: B_DARK_BORDER_STRONG,
  accent: B_DARK_ACCENT,
  danger: B_DARK_DANGER,
  warning: B_DARK_WARNING,
  success: B_DARK_SUCCESS
});
var spaciousPremiumArtisticLight = buildBranding({
  id: "norkron/canonical-variant-c-spacious-premium-artistic-light",
  surfaceBase: C_LIGHT_SURFACE_BASE,
  surfaceElevated: C_LIGHT_SURFACE_ELEVATED,
  surfaceSunken: C_LIGHT_SURFACE_SUNKEN,
  overlay: "rgba(26,18,7,0.55)",
  inverse: C_DARK_SURFACE_BASE,
  textPrimary: C_LIGHT_TEXT_PRIMARY,
  textSecondary: C_LIGHT_TEXT_SECONDARY,
  textMuted: C_LIGHT_TEXT_MUTED,
  textDisabled: C_LIGHT_TEXT_DISABLED,
  textInverse: C_DARK_TEXT_PRIMARY,
  link: C_LIGHT_ACCENT,
  borderSubtle: C_LIGHT_BORDER_SUBTLE,
  borderStrong: C_LIGHT_BORDER_STRONG,
  accent: C_LIGHT_ACCENT,
  danger: C_LIGHT_DANGER,
  warning: C_LIGHT_WARNING,
  success: C_LIGHT_SUCCESS
});
var spaciousPremiumArtisticDark = buildBranding({
  id: "norkron/canonical-variant-c-spacious-premium-artistic-dark",
  surfaceBase: C_DARK_SURFACE_BASE,
  surfaceElevated: C_DARK_SURFACE_ELEVATED,
  surfaceSunken: C_DARK_SURFACE_SUNKEN,
  overlay: "rgba(13,5,17,0.7)",
  inverse: C_LIGHT_SURFACE_BASE,
  textPrimary: C_DARK_TEXT_PRIMARY,
  textSecondary: C_DARK_TEXT_SECONDARY,
  textMuted: C_DARK_TEXT_MUTED,
  textDisabled: C_DARK_TEXT_DISABLED,
  textInverse: C_LIGHT_TEXT_PRIMARY,
  link: C_DARK_ACCENT,
  borderSubtle: C_DARK_BORDER_SUBTLE,
  borderStrong: C_DARK_BORDER_STRONG,
  accent: C_DARK_ACCENT,
  danger: C_DARK_DANGER,
  warning: C_DARK_WARNING,
  success: C_DARK_SUCCESS
});
function getVariantExtraCssVars(variant, mode) {
  if (variant !== "spaciousPremiumArtistic") return /* @__PURE__ */ new Map();
  return /* @__PURE__ */ new Map([
    [
      "--surface-decoration-gradient",
      mode === "light" ? C_LIGHT_DECOR_GRADIENT : C_DARK_DECOR_GRADIENT
    ],
    [
      "--shadow-elevation-3",
      mode === "light" ? C_LIGHT_SHADOW_ELEVATION_3 : C_DARK_SHADOW_ELEVATION_3
    ]
  ]);
}
function getCanonicalVariant(variant, mode) {
  if (variant === "minimalisticCompact") {
    return mode === "light" ? minimalisticCompactLight : minimalisticCompactDark;
  }
  if (variant === "modernAverage") {
    return mode === "light" ? modernAverageLight : modernAverageDark;
  }
  return mode === "light" ? spaciousPremiumArtisticLight : spaciousPremiumArtisticDark;
}
var CANONICAL_VARIANTS = [
  "minimalisticCompact",
  "modernAverage",
  "spaciousPremiumArtistic"
];
var WCAG_AA_BODY = 4.5;
function verifyVariantContrast(variant, mode) {
  const cfg = getCanonicalVariant(variant, mode);
  const text = cfg.colors.text;
  const surfaces = cfg.colors.surfaces;
  const state = cfg.colors.state;
  const pairs = [
    {
      label: `${variant}/${mode}/text-primary on surface-base`,
      fg: text.primary.value,
      bg: surfaces.base.value
    },
    {
      label: `${variant}/${mode}/text-primary on surface-elevated`,
      fg: text.primary.value,
      bg: surfaces.elevated.value
    },
    {
      label: `${variant}/${mode}/text-secondary on surface-base`,
      fg: text.secondary.value,
      bg: surfaces.base.value
    },
    {
      label: `${variant}/${mode}/text-muted on surface-base`,
      fg: text.muted.value,
      bg: surfaces.base.value
    },
    {
      label: `${variant}/${mode}/text-link on surface-base`,
      fg: text.link.value,
      bg: surfaces.base.value
    },
    {
      label: `${variant}/${mode}/state-danger on surface-base`,
      fg: state.danger.value,
      bg: surfaces.base.value
    },
    {
      label: `${variant}/${mode}/state-warn on surface-base`,
      fg: state.warn.value,
      bg: surfaces.base.value
    },
    {
      label: `${variant}/${mode}/state-success on surface-base`,
      fg: state.success.value,
      bg: surfaces.base.value
    }
  ];
  const failures = [];
  for (const p of pairs) {
    const ratio = contrastRatio(p.fg, p.bg);
    if (ratio < WCAG_AA_BODY) {
      failures.push({ label: p.label, ratio, required: WCAG_AA_BODY });
    }
  }
  return {
    variant,
    mode,
    passed: failures.length === 0,
    checked: pairs.length,
    failures
  };
}
function assertAllVariantsAreAA() {
  for (const variant of CANONICAL_VARIANTS) {
    for (const mode of ["light", "dark"]) {
      const r = verifyVariantContrast(variant, mode);
      if (!r.passed) {
        const summary = r.failures.map((f) => `${f.label}: ${f.ratio.toFixed(2)}:1 < ${f.required}:1`).join("; ");
        throw new Error(
          `canonical-variants: WCAG AA contrast regressed for ${r.variant}/${r.mode} (${r.failures.length}/${r.checked}): ${summary}`
        );
      }
    }
  }
}
function deepFreeze(value) {
  if (value === null || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.freeze(value);
  for (const key of Object.getOwnPropertyNames(value)) {
    deepFreeze(value[key]);
  }
  return value;
}
for (const cfg of [
  minimalisticCompactLight,
  minimalisticCompactDark,
  modernAverageLight,
  modernAverageDark,
  spaciousPremiumArtisticLight,
  spaciousPremiumArtisticDark
]) {
  deepFreeze(cfg);
}
assertAllVariantsAreAA();
var ACCENT_TOKEN_NAMES = [
  "--norkron-accent",
  "--norkron-color-accent",
  "--norkron-link",
  "--norkron-focus-ring-color",
  "--norkron-text-on-accent"
];
var DEFAULT_TEXT_LIGHT = "#ffffff";
var DEFAULT_TEXT_DARK = "#1f2937";
var THEMES = ["light", "dark"];
function resolveOptions(opts) {
  const largeText = opts?.largeText ?? false;
  return {
    minGrade: opts?.minGrade ?? "AA",
    largeText,
    textTokens: {
      light: opts?.textTokens?.light ?? DEFAULT_TEXT_LIGHT,
      dark: opts?.textTokens?.dark ?? DEFAULT_TEXT_DARK
    },
    enforce: opts?.enforce ?? "WCAG",
    minApcaLc: opts?.minApcaLc ?? (largeText ? 45 : 60),
    onFail: opts?.onFail ?? "throw"
  };
}
function requiredRatio(minGrade, largeText) {
  if (largeText) return minGrade === "AAA" ? 4.5 : 3;
  return minGrade === "AAA" ? 7 : 4.5;
}
function gradeFor(ratio, largeText) {
  const raw = wcagGrade(ratio, { largeText });
  return raw === "AAA" || raw === "AA" ? raw : "fail";
}
var HEX_RE = /^#?(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
function assertHex(value, where) {
  if (typeof value !== "string" || !HEX_RE.test(value.trim())) {
    throw new Error(
      `defineAccentScale: ${where} must be a 3- or 6-digit hex colour, received ${JSON.stringify(value)}.`
    );
  }
  return value.trim();
}
function suggestPassingStop(stop, o, needRatio) {
  const [h, s, l] = hexToHsl(stop);
  const baseL = Math.round(l);
  let best;
  for (let delta = 1; delta <= 100; delta++) {
    for (const signed of [-delta, delta]) {
      const candidateL = baseL + signed;
      if (candidateL < 0 || candidateL > 100) continue;
      const hex = hslToHex(h, s, candidateL);
      const measured = measurePair(hex, o);
      if (measured.ratio >= needRatio && passesApca(measured.apca, o)) {
        best = { hex, deltaL: signed, ratio: measured.ratio, on: measured.on };
        break;
      }
    }
    if (best) break;
  }
  return best;
}
function passesApca(apca, o) {
  if (o.enforce === "WCAG") return true;
  return Math.abs(apca) >= o.minApcaLc;
}
function measurePair(stop, o) {
  const lightRatio = contrastRatio(stop, o.textTokens.light);
  const darkRatio = contrastRatio(stop, o.textTokens.dark);
  const lightWins = lightRatio > darkRatio;
  const on = lightWins ? o.textTokens.light : o.textTokens.dark;
  const ratio = lightWins ? lightRatio : darkRatio;
  const runnerUp = lightWins ? { on: o.textTokens.dark, ratio: darkRatio } : { on: o.textTokens.light, ratio: lightRatio };
  return { on, ratio, apca: apcaContrast(on, stop), runnerUp };
}
function buildRow(name, theme, stop, o, need) {
  const m = measurePair(stop, o);
  const grade = gradeFor(m.ratio, o.largeText);
  const apcaPass = Math.abs(m.apca) >= o.minApcaLc;
  const wcagOk = m.ratio >= need;
  const passes = o.enforce === "WCAG" ? wcagOk : o.enforce === "APCA" ? apcaPass : wcagOk && apcaPass;
  const [L, C, H] = hexToOklch(stop);
  return {
    name,
    theme,
    stop,
    on: m.on,
    ratio: round2(m.ratio),
    grade,
    wcagGradeRaw: wcagGrade(m.ratio, { largeText: o.largeText }),
    apca: round2(m.apca),
    apcaPass,
    passes,
    required: need,
    polarity: m.on === o.textTokens.light ? "light-on-dark" : "dark-on-light",
    oklch: [round4(L), round4(C), round4(H)],
    runnerUp: { on: m.runnerUp.on, ratio: round2(m.runnerUp.ratio) }
  };
}
function round2(n) {
  return Math.round(n * 100) / 100;
}
function round4(n) {
  return Math.round(n * 1e4) / 1e4;
}
function formatFailure(f) {
  const suffix = f.suggestion ? ` \u2014 nearest passing same-hue stop: ${f.suggestion.hex} (HSL L ${f.suggestion.deltaL > 0 ? "+" : ""}${f.suggestion.deltaL}, ${f.suggestion.ratio.toFixed(2)}:1 on ${f.suggestion.on})` : " \u2014 no same-hue stop in the sRGB gamut reaches the floor; the hue itself must change";
  const wcagPart = `${f.ratio.toFixed(2)}:1 < ${f.required}:1 (best token ${f.on})`;
  const apcaPart = f.failedUnder.includes("APCA") ? `, APCA |Lc| ${Math.abs(f.apca).toFixed(1)} < ${f.requiredApca}` : "";
  return `${f.name}/${f.theme} ${f.stop}: ${wcagPart}${apcaPart}${suffix}`;
}
function defineAccentScale(input, opts) {
  validateInput(DefineAccentScaleInputSchema, "defineAccentScale", { input, opts });
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    throw new Error(
      `defineAccentScale: input must be an object of named { light, dark } stops, received ${JSON.stringify(input)}.`
    );
  }
  const names = Object.keys(input);
  if (names.length === 0) {
    throw new Error("defineAccentScale: input defines no hues; an empty scale cannot be audited.");
  }
  const o = resolveOptions(opts);
  const need = requiredRatio(o.minGrade, o.largeText);
  assertHex(o.textTokens.light, "textTokens.light");
  assertHex(o.textTokens.dark, "textTokens.dark");
  const rows = [];
  const failures = [];
  for (const name of names) {
    const stops = input[name];
    if (stops === null || typeof stops !== "object") {
      throw new Error(
        `defineAccentScale: hue "${name}" must be an object with light + dark hex stops, received ${JSON.stringify(stops)}.`
      );
    }
    for (const theme of THEMES) {
      const stop = assertHex(stops[theme], `hue "${name}" ${theme} stop`);
      const row = buildRow(name, theme, stop, o, need);
      rows.push(row);
      if (!row.passes) {
        const failedUnder = [];
        if (o.enforce !== "APCA" && row.ratio < need) failedUnder.push("WCAG");
        if (o.enforce !== "WCAG" && !row.apcaPass) failedUnder.push("APCA");
        failures.push({
          name,
          theme,
          stop,
          on: row.on,
          ratio: row.ratio,
          required: need,
          apca: row.apca,
          requiredApca: o.minApcaLc,
          failedUnder,
          suggestion: suggestPassingStop(stop, o, need)
        });
      }
    }
  }
  if (failures.length > 0) {
    const summary = failures.map(formatFailure).join("\n  ");
    const message = `defineAccentScale: ${failures.length} of ${rows.length} accent cells cannot reach ${o.minGrade}${o.largeText ? " (large text)" : ""} under ${o.enforce} against either text token (${o.textTokens.light} / ${o.textTokens.dark}):
  ${summary}`;
    try {
      warn({
        fn: "defineAccentScale",
        code: "THEMER_ACCENT_CONTRAST_FAIL",
        message,
        expected: `every accent stop to reach ${need}:1 against one of the text tokens`,
        received: failures.map((f) => `${f.name}/${f.theme}=${f.ratio}`),
        dedupKey: failures.map((f) => `${f.name}/${f.theme}`).join(","),
        severity: "critical-but-non-throwing"
      });
    } catch {
    }
    if (o.onFail === "throw") throw new Error(message);
  }
  const frozenRows = Object.freeze(rows.map((r) => Object.freeze(r)));
  const frozenFailures = Object.freeze(
    failures.map((f) => Object.freeze(f))
  );
  const byKey = new Map(frozenRows.map((r) => [`${r.name}\0${r.theme}`, r]));
  function rowFor(name, theme) {
    const row = byKey.get(`${name}\0${theme}`);
    if (!row) {
      throw new Error(
        `AccentScale: unknown hue "${name}" (defined: ${names.join(", ")}).`
      );
    }
    return row;
  }
  const scale = {
    names: () => Object.freeze([...names]),
    audit: () => frozenRows,
    has: (name) => byKey.has(`${name}\0light`),
    stop: (name, theme) => rowFor(name, theme).stop,
    on: (name, theme) => rowFor(name, theme).on,
    vars(name, theme) {
      const row = rowFor(name, theme);
      return {
        "--norkron-accent": row.stop,
        // Emitted literally, NOT left to base.css's var() alias — see the
        // module header on scoped accents.
        "--norkron-color-accent": row.stop,
        "--norkron-link": row.stop,
        "--norkron-focus-ring-color": row.stop,
        "--norkron-text-on-accent": row.on
      };
    },
    toCss(options) {
      const attribute = options?.attribute ?? "data-accent";
      const themeAttribute = options?.themeAttribute ?? "data-theme";
      const indent = options?.indent ?? "	";
      const trailing = options?.trailingNewline ?? true;
      const blocks = [];
      for (const name of names) {
        for (const theme of THEMES) {
          const selector = theme === "light" ? `[${attribute}="${name}"]` : `[${themeAttribute}="dark"] [${attribute}="${name}"], [${themeAttribute}="dark"][${attribute}="${name}"]`;
          const pairs = Object.entries(scale.vars(name, theme)).map(([k, v]) => `${indent}${k}: ${v};`).join("\n");
          blocks.push(`${selector} {
${pairs}
}`);
        }
      }
      return blocks.join("\n\n") + (trailing ? "\n" : "");
    },
    describe() {
      const worstRow = frozenRows.reduce((a, b) => b.ratio < a.ratio ? b : a);
      return {
        names: Object.freeze([...names]),
        tokenNames: ACCENT_TOKEN_NAMES,
        minGrade: o.minGrade,
        largeText: o.largeText,
        enforce: o.enforce,
        requiredRatio: need,
        requiredApca: o.minApcaLc,
        textTokens: { ...o.textTokens },
        cellCount: frozenRows.length,
        worst: {
          name: worstRow.name,
          theme: worstRow.theme,
          ratio: worstRow.ratio,
          grade: worstRow.grade
        },
        failing: Object.freeze(
          frozenRows.filter((r) => !r.passes).map((r) => ({ name: r.name, theme: r.theme, ratio: r.ratio }))
        )
      };
    },
    failures: frozenFailures
  };
  return Object.freeze(scale);
}
function accentVars(scale, name, theme) {
  validateInput(AccentVarsInputSchema, "accentVars", { name, theme });
  if (!scale || typeof scale.vars !== "function") {
    throw new Error("accentVars: first argument must be an AccentScale from defineAccentScale().");
  }
  return scale.vars(name, theme);
}
function applyAccent(scale, name, theme, target) {
  validateInput(ApplyAccentInputSchema, "applyAccent", { name, theme });
  if (!scale || typeof scale.vars !== "function") {
    throw new Error("applyAccent: first argument must be an AccentScale from defineAccentScale().");
  }
  const el = target ?? (typeof document !== "undefined" ? document.documentElement : void 0);
  if (!el || typeof el.style?.setProperty !== "function") return;
  for (const [token, value] of Object.entries(scale.vars(name, theme))) {
    el.style.setProperty(token, value);
  }
}
function assertAccentScaleGrade(scale, minGrade = "AA") {
  validateInput(AssertAccentScaleGradeInputSchema, "assertAccentScaleGrade", { minGrade });
  if (!scale || typeof scale.audit !== "function") {
    throw new Error(
      "assertAccentScaleGrade: first argument must be an AccentScale from defineAccentScale()."
    );
  }
  const rank = { fail: 0, AA: 1, AAA: 2 };
  const bad = scale.audit().filter((r) => rank[r.grade] < rank[minGrade]);
  if (bad.length > 0) {
    const table = bad.map(
      (r) => `${r.name}/${r.theme} ${r.stop} on ${r.on}: ${r.ratio.toFixed(2)}:1 (${r.wcagGradeRaw}), APCA ${r.apca.toFixed(1)}`
    ).join("\n  ");
    throw new Error(
      `assertAccentScaleGrade: ${bad.length} of ${scale.audit().length} cells below ${minGrade}:
  ${table}`
    );
  }
}

// src/index.ts
try {
  registerLazy(
    "@norkron/web-themer",
    () => selfCheck
  );
} catch {
}

export { ACCENT_TOKEN_NAMES, BREAKPOINT_ORDER, CANONICAL_BREAKPOINTS, CANONICAL_BREAKPOINT_PX, CANONICAL_VARIANTS, LIB_VERSION, PRESETS, accentVars, applyAccent, applyBrandingConfig, assertAccentScaleGrade, assertAllVariantsAreAA, brutalist, currentBreakpoint, defineAccentScale, denseData, describeBrandingConfig, editorial, emitCssBlock, emitVariablePairs, geological, getCanonicalVariant, getPreset, getThemeBootstrapScript, getVariantExtraCssVars, isCanonicalBreakpointPx, learnBrandingConfig, matchesBreakpoint, minimalisticCompactDark, minimalisticCompactLight, modernAverageDark, modernAverageLight, nearestCanonicalBreakpoint, nextBreakpointUp, playfulNative, premiumRestrained, selfCheck, spaciousPremiumArtisticDark, spaciousPremiumArtisticLight, verifyVariantContrast };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map